import { useEffect, useState } from "react";
import { api, ApiError, type AvatarSummary } from "./api.ts";

export type Template = {
  slug: string;
  name: string;
  voice: string;
  picture?: string;
  jobTitle: string;
  tagline: string;
  jobDescription: string;
  capabilities: string[];
  resources: { kind: string; label: string }[];
};

const TOOL_LABEL: Record<string, string> = {
  delegate_to_brain: "memory & tasks",
  remember_preference: "learns your preferences",
  web_search: "web access",
};

/**
 * Hire a predefined avatar.
 *
 * The capability chips are not decoration: a template without web access
 * genuinely cannot look anything up, and its briefing says so. Showing them on
 * the card is what makes that difference visible before hiring rather than
 * discovered mid-conversation.
 */
export function Marketplace({
  onClose,
  onHired,
}: {
  onClose: () => void;
  onHired: (id: string) => void;
}) {
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      api<{ templates: Template[] }>(`/api/marketplace?q=${encodeURIComponent(query)}`)
        .then((d) => setTemplates(d.templates))
        .catch((e) => setError(e instanceof ApiError ? e.message : "could not load the marketplace"));
    }, query ? 180 : 0);
    return () => clearTimeout(id);
  }, [query]);

  const hire = async (t: Template) => {
    setBusy(t.slug);
    setError(null);
    try {
      const { avatar } = await api<{ avatar: AvatarSummary }>("/api/avatars", {
        body: { templateSlug: t.slug },
      });
      onHired(avatar.id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "could not hire");
      setBusy(null);
    }
  };

  return (
    <div className="modal" onClick={onClose}>
      <div className="panel market" onClick={(e) => e.stopPropagation()}>
        <h2>Marketplace</h2>
        <p className="muted">
          Each one arrives knowing its job and only the tools that job needs. It will decline
          work outside it.
        </p>
        <input
          className="market-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or job…"
        />
        {error && <div className="error">⚠ {error}</div>}
        {!templates && !error && <p className="muted">Loading…</p>}

        {templates?.length === 0 && <p className="muted">Nothing matches “{query}”.</p>}
        <div className="cards">
          {templates?.map((t) => (
            <div key={t.slug} className="card">
              <div className="card-top">
                {t.picture ? (
                  <img src={t.picture} alt="" className="portrait" />
                ) : (
                  <div className="portrait fallback">{t.name.slice(0, 2).toUpperCase()}</div>
                )}
                <div>
                  <div className="card-name">{t.name}</div>
                  <div className="card-job">{t.jobTitle}</div>
                  <div className="card-voice">voice: {t.voice}</div>
                </div>
              </div>
              <p className="card-tagline">{t.tagline}</p>
              <div className="chips">
                {t.capabilities.map((c) => (
                  <span key={c} className="cap">
                    {TOOL_LABEL[c] ?? c}
                  </span>
                ))}
                {t.resources.map((r) => (
                  <span key={r.kind} className="cap resource">
                    {r.label}
                  </span>
                ))}
              </div>
              <button onClick={() => void hire(t)} disabled={busy !== null}>
                {busy === t.slug ? "Hiring…" : `Hire ${t.name}`}
              </button>
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button className="ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
